/**
 * @file: Where a dragged row lands.
 *
 * A drop happens at a boundary between two rows, and not on a row. A boundary
 * is a number from 0 to n. It is the index the dragged row lands before, so
 * boundary 0 is above the first row and boundary n is below the last.
 *
 * A move down takes the row out of the list first, so every row after it moves
 * up one place. The final index of the row is then `boundary - 1`. A move up
 * takes rows that are all after the boundary, so the final index is the
 * boundary itself.
 *
 * The server takes a source id, a target id and a side. `moveIndices` gives all
 * three of them from a boundary, and `insertionIndexFor` turns the side back
 * into an index on the server.
 *
 * The server derived the side from the index of the source before. That rule
 * held only while both sides of the wire ordered the list the same way, and two
 * rows with an equal order value broke it. The side now travels with the call.
 *
 * The module is shared because the client and the server must agree on the
 * side. Two implementations drift, and then a drop lands in the wrong place.
 */

/** The two sides of a target row that a drop can land on. */
const DROP_SIDES = Object.freeze(['before', 'after'])

function unknownDropSide (side) {
  return new Error(`Unknown drop side: ${side}. Use one of: ${DROP_SIDES.join(', ')}`)
}

/**
 * The move a boundary asks for, or null when the row would not move.
 *
 * `to` is where the row goes in the list it was taken out of, which is what a
 * local list splices at. `targetIndex` names the row the call travels with, in
 * the list as it is now. `side` says which side of that row the dragged row
 * lands on, and `insertionIndexFor` turns the word back into an index.
 *
 * A boundary at the place the row already has, or at the one just after it, is
 * no move: the row would land where it already is.
 *
 * @param {number} from - The index the dragged row has now
 * @param {number} boundary - The boundary the pointer is at
 * @returns {object|null} `{ to, targetIndex, side }`, or null for no move
 */
function moveIndices (from, boundary) {
  if (boundary === from || boundary === from + 1) {
    return null
  }

  return boundary < from
    ? { to: boundary, targetIndex: boundary, side: DROP_SIDES[0] }
    : { to: boundary - 1, targetIndex: boundary - 1, side: DROP_SIDES[1] }
}

/**
 * Where the source goes when the caller names the side of the target.
 *
 * The operator sees a line at a boundary, and the side says which row that line
 * was beside. The index of the source has no part in it, so two rows with an
 * equal order value cannot move the drop.
 *
 * @param {string} side - 'before' or 'after'
 * @param {number} targetIndex - The index of the target in the list without the source
 * @returns {number} The index to insert the source at
 */
function insertionIndexFor (side, targetIndex) {
  if (side === 'before') {
    return targetIndex
  }

  if (side === 'after') {
    return targetIndex + 1
  }

  throw unknownDropSide(side)
}

/**
 * Guard for the side of a payload.
 *
 * Every caller sends a side. A missing or wrong value is a fault of the caller,
 * and a guess would move a row to a place the operator did not choose.
 *
 * @param {string} side - The side from the payload
 */
function checkDropSide (side) {
  if (!DROP_SIDES.includes(side)) {
    throw unknownDropSide(side)
  }
}

export {
  DROP_SIDES,
  moveIndices,
  insertionIndexFor,
  checkDropSide
}
